<template>
  <div class="layer">
    <div class="inner">
      <button type="button" class="btnClose" @click="onClose"><span class="blind">X</span></button>
      <h2 class="layerTitle toCapitalize">{{locale.tv_setting_controller}}</h2>
      <div class="settingBox">
        <div class="remoteList">
          <select class="input mb5" v-model="remocon">
            <option value="">{{locale.tv_select_remocon}}</option>
            <option v-for="item in remoconList"
              :key="item"
              :value="item">{{item}}</option>
          </select>
          <div v-if="remocon !== ''" class="listBox">
            <div v-if="activeRemote.length > 1" class="breakBtns">
              <button type="button" class="btn btnGrey sizeXs mb5 toCapitalize" @click="addGroup">{{locale.tv_remocon_break}}</button>
              <button type="button" class="btn sizeXs mb5 toCapitalize" @click="addEmpty">{{locale.tv_remocon_add_empty}}</button>
            </div>
            <draggable class="list"
              tag="div"
              v-model="remoteList"
              v-bind="dragOptions">
              <transition-group type="transition" :name="!drag ? 'flip-list' : null">
                <div class="item"
                  v-for="item in remoteList"
                  :key="item.id"
                  :class="{
                    active: item.active,
                    iconInput: isIcon(item.id) || isColor(item.id),
                    defaultGroup: defaultGroup(item.id),
                    remoteGroup: remoteGroup(item.id),
                    remoteEmpty: remoteEmpty(item.id)
                  }">
                  <div v-if="item.id === 'default_controls'" class="controlGroup">
                    <span class="checkbox">
                      <input type="checkbox"
                        :id="`remote_${item.id}`"
                        v-model="item.active"
                        @change="sortList">
                      <label :for="`remote_${item.id}`"></label>
                    </span>
                  </div>
                  <div v-else-if="item.id === 'default_arrows'" class="arrowGroup">
                    <span class="checkbox">
                      <input type="checkbox"
                        :id="`remote_${item.id}`"
                        v-model="item.active"
                        @change="sortList">
                      <label :for="`remote_${item.id}`"></label>
                    </span>
                  </div>
                  <div v-else-if="remoteGroup(item.id)" class="division">
                    <button type="button" class="btnRemove" @click="removeGroup(item.id)"></button>
                  </div>
                  <div v-else-if="remoteEmpty(item.id)" class="empty">
                    <button type="button" class="btnRemove" @click="removeGroup(item.id)"></button>
                  </div>
                  <div v-else>
                    <span class="checkbox">
                      <input type="checkbox"
                        :id="`remote_${item.id}`"
                        v-model="item.active"
                        @change="sortList">
                      <label :for="`remote_${item.id}`"></label>
                    </span>
                    <div class="info">
                      <i>?</i>
                      <div class="exp">{{item.id}}</div>
                    </div>
                    <input type="text" class="input"
                      v-model="item.name"
                      :disabled="isIcon(item.id) || isColor(item.id)" />
                  </div>
                </div>
              </transition-group>
            </draggable>
          </div>
          <p v-else class="msg">{{locale.tv_remocon_message}}</p>
        </div>
        <div class="remoteController">
          <Controller
            :locale="locale"
            :activeRemote="activeRemote"
            :remoteIcon="remoteIcon"
            :colors="colors"
            :remocon="remocon"
            @changeGroup="changeRemote" />
        </div>
      </div>
      <div class="sepApply">
        <button type="button" class="btn btnPrimary btnFull toUppercase" @click="onApply">{{locale.apply}}</button>
      </div>
    </div>
  </div>
</template>

<script>
import UNIQUE from '@/common/unique'
import Controller from '@/components/Tv/Controller.vue'
import draggable from 'vuedraggable'

export default {
  props: [
    'locale'
  ],
  components: {
    Controller,
    draggable
  },
  computed: {
    dragOptions () {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost'
      }
    },
    remocons () {
      return this.$store.getters['tv/remoconList']
    },
    remoconList () {
      return this.remocons.map(x => x.model)
    },
    remocon: {
      get () {
        return this.setting.remocon || ''
      },
      set (remocon) {
        this.$store.commit('tv/setSetting', {
          remocon: remocon
        })
        const pairs = this.remocons.find(x => x.model === remocon).pairingRule
        const remoteList = Object.keys(pairs).map(x => {
          return {
            active: false,
            id: x,
            name: x
          }
        })
        if (window.confirm(this.locale.tv_confirm_change_remocon)) {
          this.remoteList = this.defaultRemote.concat(remoteList)
        }
      }
    },
    remoteIcon () {
      return this.$store.state.tv.setting.remoteIcon
    },
    setting () {
      return this.$store.getters['tv/getSetting']
    },
    activeRemote () {
      return this.remoteList.filter(x => x.active)
    }
  },
  created () {
    this.$store.commit('onLoading', true)
    this.$store.dispatch('tv/getSetting').then(() => {
      this.$store.dispatch('tv/getRemocon').then(() => {
        this.init()
        this.$store.commit('onLoading', false)
      })
    })
  },
  data () {
    return {
      drag: false,
      remoteList: [],
      defaultRemote: [
        { id: 'default_controls', active: true },
        { id: 'default_arrows', active: true }
      ],
      defaultButtons: [
        'power',
        'volume_up',
        'volume_down',
        'channel_up',
        'channel_down',
        'navigate_right',
        'navigate_left',
        'navigate_down',
        'navigate_up',
        'mute',
        'menu',
        'ok'
      ],
      colors: [
        'red',
        'blue',
        'yellow',
        'green'
      ]
    }
  },
  methods: {
    init () {
      const activeList = this.setting.remoteList.length > 0 ? this.setting.remoteList : this.defaultRemote
      const remocon = this.remocon
      const legacyList = this.remocons.find(x => x.model === remocon) || {}
      const pairs = legacyList.pairingRule || {}
      const activeIds = activeList.map(x => x.id)
      // 전체 pairs 중 활성화되지 않은 키 배열
      const remoteList = Object.keys(pairs).map(x => {
        return {
          active: false,
          id: x,
          name: x
        }
      }).filter(x => !activeIds.includes(x.id))
      // 기본 버튼 추가
      this.defaultRemote.forEach(function (x) {
        if (!activeList.find(a => a.id === x.id)) {
          remoteList.unshift({ ...x, active: false })
        }
      })
      this.remoteList = activeList.concat(remoteList)
    },
    changeRemote (group) {
      this.remoteList = group
    },
    defaultGroup (id) {
      return id.includes('default_controls') || id.includes('default_arrows')
    },
    remoteGroup (id) {
      return id.includes('remote_group')
    },
    remoteEmpty (id) {
      return id.includes('remote_empty')
    },
    isIcon (id) {
      return this.remoteIcon.includes(id)
    },
    isColor (id) {
      return this.colors.includes(id)
    },
    sortList () {
      this.remoteList.sort((a, b) => a.active === b.active ? 0 : a.active ? -1 : 1)
    },
    addGroup () {
      const i = UNIQUE.fileid()
      const active = this.remoteList.filter(x => x.active)
      const inActive = this.remoteList.filter(x => !x.active)
      const group = [{
        id: `remote_group_${i}`,
        active: true
      }]
      this.remoteList = active.concat(group).concat(inActive)
    },
    addEmpty () {
      const i = UNIQUE.fileid()
      const active = this.remoteList.filter(x => x.active)
      const inActive = this.remoteList.filter(x => !x.active)
      const empty = [{
        id: `remote_empty_${i}`,
        active: true
      }]
      this.remoteList = active.concat(empty).concat(inActive)
    },
    removeGroup (id) {
      this.remoteList = this.remoteList.filter(x => x.id !== id)
    },
    onClose () {
      this.$emit('closeRemote')
    },
    onApply () {
      if (window.confirm(this.locale.confirm_save_tvcontroller)) {
        this.$store.commit('tv/setSetting', { remoteList: this.activeRemote })
        this.$store.dispatch('tv/setSetting', this.setting).then(() => {
          alert(this.locale.complete_save)
        })
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.layer .inner {
  max-width: 800px;
}
.msg {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding-bottom: 30%;
}
.settingBox {
  display: flex;
  overflow: hidden;
  .remoteController {
    margin-left: auto;
    overflow: auto;
  }
}
.remoteList {
  flex-basis: 300px;
  max-height: 100%;
  .listBox {
    height: 100%;
    padding-bottom: 45px;
  }
  .list {
    max-height: 100%;
    padding-bottom: 28px;
    overflow: auto;
  }
  .item {
    position: relative;
    padding: 8px 30px 8px 45px;
    border: 1px solid $color-lgrey2;
    background-color: $color-white;
    cursor: move;
    .input {
      min-height: 0;
      padding: 0.3em 0.5em;
      opacity: 0.5;
      background-color: $color-lgrey2;
    }
    .checkbox {
      position: absolute;
      top: 50%;
      left: 10px;
      margin-top: -11px;
    }
    & + .item {
      border-top: none;
    }
    &.active .input {
      opacity: 1;
      background-color: $color-white;
    }
    .info {
      position: absolute;
      top: 50%;
      right: 35px;
      margin-top: -8px;
      z-index: 1;
      i {
        display: block;
        width: 15px;
        height: 15px;
        text-align: center;
        font-size: 12px;
        line-height: 15px;
        border: 1px solid $color-lgrey;
        border-radius: 100%;
        background-color: $color-white;
      }
      .exp {
        display: none;
        position: absolute;
        bottom: 100%;
        right: -15px;
        margin-bottom: 6px;
        padding: 2px 10px;
        font-size: 12px;
        color: $color-white;
        white-space: nowrap;
        border-radius: 2px;
        background-color: $color-dark;
        z-index: 1;
        &:before {
          content: "";
          position: absolute;
          top: 100%;
          right: 20px;
          width: 6px;
          height: 6px;
          margin-top: -3px;
          background-color: $color-dark;
          transform: rotate(45deg);
        }
      }
      &:hover .exp {
        display: block;
      }
    }
  }
  .defaultGroup {
    padding: 15px 0;
    .controlGroup,
    .arrowGroup {
      position: relative;
      &:before {
        content: "";
        display: block;
        width: 100%;
        height: 100px;
        background-size: contain;
        background-position: 50% 50%;
        background-repeat: no-repeat;
        opacity: 0.5;
      }
    }
    .controlGroup:before {
      background-image: url('~@/assets/img/tvcontroller/ico_controlGroup.png');
    }
    .arrowGroup:before {
      background-image: url('~@/assets/img/tvcontroller/ico_arrowGroup.png');
    }
  }
  .remoteEmpty {
    padding: 10px 0;
    border-top: none;
    border-left: none;
    border-right: none;
    background-color: transparent;
    .empty {
      border-top: 15px dashed $color-lgrey2;
    }
    .btnRemove {
      display: none;
      position: absolute;
      top: 0;
      right: 0;
      width: 20px;
      height: 100%;
      &:before,
      &:after {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        background-color: $color-dark;
        transform: rotate(45deg);
      }
      &:before {
        width: 14px;
        height: 2px;
        margin-top: -1px;
        margin-left: -7px;
      }
      &:after {
        width: 2px;
        height: 14px;
        margin-top: -7px;
        margin-left: -1px;
      }
    }
    &:hover {
      padding-right: 22px;
      .btnRemove {
        display: block;
      }
    }
  }
  .remoteGroup {
    padding: 10px 0;
    border-top: none;
    border-left: none;
    border-right: none;
    background-color: transparent;
    .division {
      border-top: 2px dashed $color-lgrey;
    }
    .btnRemove {
      display: none;
      position: absolute;
      top: 0;
      right: 0;
      width: 20px;
      height: 100%;
      &:before,
      &:after {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        background-color: $color-dark;
        transform: rotate(45deg);
      }
      &:before {
        width: 14px;
        height: 2px;
        margin-top: -1px;
        margin-left: -7px;
      }
      &:after {
        width: 2px;
        height: 14px;
        margin-top: -7px;
        margin-left: -1px;
      }
    }
    &:hover {
      padding-right: 22px;
      .btnRemove {
        display: block;
      }
    }
  }
  .iconInput {
    .input {
      padding-left: calc(30px + 1em);
    }
    &:before {
      content: "icon";
      position: absolute;
      top: 50%;
      left: 52px;
      width: 30px;
      margin-top: -0.75em;
      text-align: center;
      font-size: 0.8em;
      color: $color-white;
      border-radius: 2px;
      background-color: $color-green;
      opacity: 0.75;
    }
    &.active:before {
      opacity: 1;
    }
  }
}
.sepApply {
  width: 180px;
  margin: 30px auto 0;
}
.breakBtns {
  display: flex;
  .btn {
    flex: 1;
    & + .btn {
      margin-left: 5px;
    }
  }
}
</style>
